import React, { useCallback, useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import { Analytics } from "../../Apis/analyticsApi"

// Auto-refresh cadence for the "Live" tab.
const LIVE_REFRESH_MS = 15000
const ROW_LIMIT = 10

// Prettify an event name: drop "$", spaces for _/-, title-case; Arabic labels
// for the few known events.
const EVENT_LABELS_AR = {
  pageview: "مشاهدة صفحة",
  pageleave: "مغادرة الصفحة",
  web_vitals: "مؤشرات أداء الويب",
  user_returned: "عودة المستخدم",
}
const prettyEvent = (raw, lang) => {
  const key = String(raw || "").replace(/^\$/, "")
  if (lang === "ar" && EVENT_LABELS_AR[key]) return EVENT_LABELS_AR[key]
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

// Relative time without the dayjs relativeTime plugin.
// PostHog returns naive UTC timestamps ("2026-07-15 11:34:57.617000") with no
// timezone marker. Parsing them directly reads them as LOCAL time, which put
// every row hours off in non-UTC timezones (e.g. "5 h ago" instead of "5 min
// ago" at UTC+5). Normalize to an explicit-UTC ISO string first.
const timeAgo = (ts, lang) => {
  if (!ts) return ""
  const iso = `${String(ts).replace(" ", "T").replace(/\.\d+$/, "")}Z`
  const then = dayjs(iso)
  if (!then.isValid()) return ""
  const secs = Math.max(0, dayjs().diff(then, "second"))
  const ar = lang === "ar"
  if (secs < 60) return ar ? "الآن" : "just now"
  const mins = Math.floor(secs / 60)
  if (mins < 60) return ar ? `منذ ${mins} دقيقة` : `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return ar ? `منذ ${hours} ساعة` : `${hours} h ago`
  return ar ? `منذ ${Math.floor(hours / 24)} يوم` : `${Math.floor(hours / 24)} d ago`
}

// Seconds -> "4m 9s" / "48s" / "1h 3m".
const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(Number(secs || 0)))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

// Deterministic dot color for a person id, echoing PostHog's person avatars.
const DOT_COLORS = ["#2563EB", "#06B6D4", "#EF4444", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899"]
const colorForId = (id) => {
  const s = String(id || "")
  let h = 0
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return DOT_COLORS[h % DOT_COLORS.length]
}

const TH = { padding: "8px 12px", fontWeight: 600, whiteSpace: "nowrap" }
const TD = { padding: "10px 12px", color: "#595959" }
const ELLIPSIS = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }

const PersonCell = ({ id }) => (
  <td style={{ ...TD }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: colorForId(id),
          color: "#fff",
          fontSize: 10,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
        }}
      >
        0
      </span>
      <span
        style={{ fontFamily: "monospace", maxWidth: 150, ...ELLIPSIS, display: "inline-block" }}
        title={id}
      >
        {id}
      </span>
    </span>
  </td>
)

const EmptyRow = ({ cols, text }) => (
  <tr>
    <td colSpan={cols} style={{ padding: "24px 0", textAlign: "center", color: "#9CA3AF" }}>
      {text}
    </td>
  </tr>
)

const PostHogActivity = ({ lang = "en", t = {}, isRTL = false }) => {
  const [tab, setTab] = useState("events")
  const [events, setEvents] = useState([])
  const [sessions, setSessions] = useState([])
  const [live, setLive] = useState([])
  const [sessionsLoaded, setSessionsLoaded] = useState(false)
  const liveTimer = useRef(null)

  const loadEvents = useCallback(async () => {
    try {
      const res = await Analytics.getPostHogActivity({ limit: ROW_LIMIT })
      setEvents(res?.data?.data || [])
    } catch {
      setEvents([])
    }
  }, [])

  const loadSessions = useCallback(async () => {
    try {
      const res = await Analytics.getPostHogSessions({ limit: ROW_LIMIT })
      setSessions(res?.data?.data || [])
    } catch {
      setSessions([])
    } finally {
      setSessionsLoaded(true)
    }
  }, [])

  const loadLive = useCallback(async () => {
    try {
      const res = await Analytics.getPostHogActivity({ limit: ROW_LIMIT })
      setLive(res?.data?.data || [])
    } catch {
      /* keep last good data on a transient failure */
    }
  }, [])

  // Initial load: Events tab.
  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  // Tab side-effects: lazy-load Sessions; poll while Live is open.
  useEffect(() => {
    if (tab === "sessions" && !sessionsLoaded) loadSessions()
    if (tab === "live") {
      loadLive()
      liveTimer.current = setInterval(loadLive, LIVE_REFRESH_MS)
      return () => clearInterval(liveTimer.current)
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const tabs = [
    { key: "events", label: t.tabEvents || "Events" },
    { key: "sessions", label: t.tabSessions || "Sessions" },
    { key: "live", label: t.tabLive || "Live" },
  ]

  const rows = tab === "sessions" ? sessions : tab === "live" ? live : events
  const count = rows.length

  const eventCols = [
    t.actEvent || "Event",
    t.actPerson || "Person",
    t.actUrl || "URL / Screen",
    t.actLibrary || "Library",
    t.actTime || "Time",
  ]
  const sessionCols = [
    t.actPerson || "Person",
    t.actEntry || "Entry Page",
    t.actPages || "Pages",
    t.actDuration || "Duration",
    t.actTime || "Time",
  ]
  const cols = tab === "sessions" ? sessionCols : eventCols

  return (
    <div
      className="bg-FFFFFF"
      style={{ borderRadius: 16, border: "1px solid #EFEFEF", padding: 20 }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Header */}
      <div style={{ textAlign: isRTL ? "right" : "left", marginBottom: 10 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1F1F1F" }}>
          {t.activity || "Activity"}
        </div>
        <div style={{ fontSize: 14, color: "#8C8C80", marginTop: 4 }}>
          {t.activitySub || "Explore events or see real-time activity from your app"}
        </div>
      </div>

      {/* Tabs + window label */}
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ borderBottom: "1px solid #F0F0F0", marginBottom: 4 }}
      >
        <div className="d-flex" style={{ gap: 18 }}>
          {tabs.map((tb) => {
            const active = tb.key === tab
            return (
              <button
                key={tb.key}
                type="button"
                onClick={() => setTab(tb.key)}
                style={{
                  background: "none",
                  border: "none",
                  padding: "8px 2px",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: active ? 700 : 500,
                  color: active ? "#0E9F9F" : "#8C8C80",
                  borderBottom: active ? "2px solid #0E9F9F" : "2px solid transparent",
                }}
              >
                {tb.label}
                {tb.key === "live" && tab === "live" ? (
                  <span
                    style={{
                      display: "inline-block",
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#EF4444",
                      marginInlineStart: 6,
                    }}
                  />
                ) : null}
              </button>
            )
          })}
        </div>
        <div style={{ fontSize: 13, color: "#8C8C80" }}>{t.lastHour || "Last hour"}</div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: "#8C8C80", textAlign: isRTL ? "right" : "left" }}>
              {cols.map((c) => (
                <th key={c} style={TH}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {count === 0 ? (
              <EmptyRow
                cols={cols.length}
                text={isRTL ? "لا يوجد نشاط حديث" : "No recent activity"}
              />
            ) : tab === "sessions" ? (
              sessions.map((s, i) => (
                <tr key={i} style={{ borderTop: "1px solid #F0F0F0" }}>
                  <PersonCell id={s.distinct_id} />
                  <td style={{ ...TD, maxWidth: 300, ...ELLIPSIS }} title={s.entry_url}>
                    {s.entry_url || "—"}
                  </td>
                  <td style={TD}>{s.pageviews}</td>
                  <td style={TD}>{fmtDuration(s.duration_sec)}</td>
                  <td style={{ ...TD, color: "#8C8C80", whiteSpace: "nowrap" }}>
                    {timeAgo(s.timestamp, lang)}
                  </td>
                </tr>
              ))
            ) : (
              rows.map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid #F0F0F0" }}>
                  <td style={{ ...TD, fontWeight: 600, color: "#1F1F1F", whiteSpace: "nowrap" }}>
                    {prettyEvent(r.event, lang)}
                  </td>
                  <PersonCell id={r.distinct_id} />
                  <td style={{ ...TD, maxWidth: 300, ...ELLIPSIS }} title={r.url}>
                    {r.url || "—"}
                  </td>
                  <td style={TD}>{r.library || "—"}</td>
                  <td style={{ ...TD, color: "#8C8C80", whiteSpace: "nowrap" }}>
                    {timeAgo(r.timestamp, lang)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {count > 0 ? (
        <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 12, textAlign: isRTL ? "right" : "left" }}>
          {(t.showingLatest || "Showing latest {n} records").replace("{n}", count)}
        </div>
      ) : null}
    </div>
  )
}

export default PostHogActivity
